import { useState, useEffect, useCallback, useMemo } from "react";
import cntl from "cntl";
import { v4 as uuidv4 } from "uuid";
import { useHistory } from "react-router";
import { UserAPI, RoleAPI } from "@griffingroupglobal/eslib-api";
import useUserPatch from "../../../hooks/useUserPatch";
import useUserDelete from "../../../hooks/useUserDelete";
import { useModalState } from "../../../state/modalState";
import handleDownloadUsers from "../../../helpers/Users/handleDownloadusers";
import { findMobileValue } from "../../../helpers/Formatters";
import { useAppState } from "../../../state/appState";
import useDirectReports from "../../../hooks/useDirectReports";
import { toastError } from "../Toast/Toast";

import { useUsers } from "../../../hooks/useUsers.new";
import useSubscription from "../../../hooks/useSubscription";

import {
  ACCOUNTANDADMIN_TABS,
  ADD_OPEN_MODAL,
  ADMIN_PATH,
  ADMIN_ROLE_NAME,
  PTO_CATEGORY,
  REMOVE_USER,
  USER_MANAGEMENT,
} from "../../../constants";
import useRoles from "../../../hooks/useRoles";
import useCurrentUser from "../../../hooks/useCurrentUser";
import useSystemConfiguration from "../../../hooks/useSystemConfiguration";

const menuButtonCN = (disabled) => cntl`
  w-64
  ${disabled && "opacity-50"}
 `;

const useMemberTableData = ({
  isExportingMemberList,
  setIsExportingMemberList,
  toastCloseIcon,
  toastErrorIcon,
}) => {
  const { data, isLoading } = useUsers();
  const users = data?.users;
  const userDict = data?.userDict;

  const [allRoles] = useRoles();
  const [, directReportOptions] = useDirectReports();
  const history = useHistory();

  const { data: currentUser } = useCurrentUser();
  const { data: originalSubscription } = useSubscription();
  const { data: systemConfiguration } = useSystemConfiguration();

  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [changeType, setChangeType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [states, setStates] = useState([]);
  const [ptoCategory, setPtoCategory] = useState();
  const [availableSeats, setAvailableSeats] = useState();

  const { mutateAsync: patchUser } = useUserPatch();
  const { mutate: deleteUser } = useUserDelete();
  const [, modalDispatch] = useModalState();

  const [{ financialsConfiguration }] = useAppState();

  const isLocked = useMemo(() => {
    return (
      financialsConfiguration?.financials?.payroll?.locked ||
      financialsConfiguration?.financials?.period?.locked
    );
  }, [financialsConfiguration]);

  // Dictionaries of company names
  const companyContacts = useMemo(() => {
    if (users?.length) {
      return users.reduce((acc, current) => {
        if (current?.kind === "company") {
          acc[current?.reference] = current?.company?.value;
        }

        return acc;
      }, {});
    }

    return [];
  }, [users]);

  // Members list
  const members = useMemo(() => {
    if (users?.length) {
      return users
        .filter((user) => user?.kind === "member")
        .map((member) => {
          const company = companyContacts[member?.company?.value];

          const stateOfEmployment = states?.find(
            (state) => state.value === member?.employeeInfo?.pto?.locationId
          )?.label;

          const hideRowAssociationMenuList =
            (currentUser?.id === member?.id || member?.isOwner) &&
            !currentUser?.isAdmin;

          return {
            ...member,
            company,
            stateOfEmployment,
            employeeId: member?.employeeInfo?.id,
            hireDate: member?.employeeInfo?.hireDate,
            hideRowAssociationMenuList,
            isCurentUser: currentUser?.id === member?.id,
            fullName: `${member?.name?.firstName} ${member?.name?.lastName}`,
            contactPointValue: findMobileValue(member?.contactPoint),
          };
        });
    }

    return [];
  }, [users, states, currentUser, companyContacts]);

  useEffect(() => {
    if (originalSubscription) {
      const seats = originalSubscription?.userCount;
      setAvailableSeats(
        seats > activeUsersCount ? seats - activeUsersCount : 0
      );
    }
  }, [activeUsersCount, originalSubscription]);

  useEffect(() => {
    setPtoCategory(systemConfiguration?.system?.pto?.category);
    if (systemConfiguration?.system?.pto?.locations?.length) {
      setStates(
        systemConfiguration?.system?.pto?.locations?.reduce(
          (acc, { id, location, isExempt }) => {
            // states DD options must have location and id
            if (id && location)
              return [
                ...acc,
                {
                  label: `${location} (${isExempt ? "Exempt" : "Non-Exempt"})`,
                  value: id,
                },
              ];
            return acc;
          },
          []
        )
      );
    }
  }, [systemConfiguration]);

  useEffect(() => {
    if (allRoles?.length) {
      setRoles(
        allRoles.map((role) => ({
          label: role.name,
          value: role.reference,
        }))
      );
    }
  }, [allRoles]);

  useEffect(() => {
    if (isExportingMemberList) {
      const formattedMembers = members.map((member) => {
        let Mobile;
        member?.contactPoint?.map((cp) => {
          if (cp?.system === "Phone") {
            Mobile = cp?.value;
          }
          return cp;
        });

        return {
          ...member,
          Company: member?.company,
          Name: `${member?.name?.firstName} ${member?.name?.lastName}`,
          Status: member?.active ? "Active" : "Inactive",
          Mobile,
          Email: member?.email,
          Role: member?.role,
        };
      });

      handleDownloadUsers(formattedMembers, "Member List");

      setIsExportingMemberList(false);
    }
  }, [isExportingMemberList, members, setIsExportingMemberList]);

  useEffect(() => {
    let count = 0;
    if (members?.length) {
      members?.forEach((member) => {
        if (member?.active) {
          count += 1;
        }
      });
    }
    setActiveUsersCount(count);
  }, [members]);

  const navigate = useCallback(() => {
    history.push(`${ADMIN_PATH}?tab=${ACCOUNTANDADMIN_TABS.BILLING_ID}`);
  }, [history]);

  const clear = useCallback(() => {
    setChangeType(null);
    setSelectedUser(null);
  }, []);

  const onRemoveUser = useCallback(
    async (userToRemove = []) => {
      // RemoveUserModal returns an array of users, however
      // Members Table allow to delete a single user per batch
      const user = userToRemove[0];

      if (!user) {
        toastError(
          "There are no users selected to remove",
          toastErrorIcon,
          toastCloseIcon
        );

        return;
      }

      // If user is active, it needs to be deactivated then removed
      if (user?.active) {
        setIsSaving(true);

        const updatedUser = {
          ...user,
          active: false,
        };

        await patchUser({
          oldUserInfo: user,
          newUserInfo: updatedUser,
        })
          .then(() => {
            deleteUser([user?.reference]);
          })
          .finally(() => {
            setIsSaving(false);
          });
      } else {
        deleteUser([user?.reference]);
      }
    },
    [deleteUser, patchUser, toastCloseIcon, toastErrorIcon]
  );

  const rowAssociationMenuList = useCallback(
    (row) => {
      /**
       * Temporary locking of state changing
       * Will be re-enabled once backend has solved this issue
       */
      const lockChangingOfStateLocation =
        row.original.registered && row.original.stateOfEmployment && isLocked;

      // find the ref for the Admin role
      const admin = roles?.find((r) => r.label === ADMIN_ROLE_NAME);
      const adminRef = admin?.value;

      const isLastAdmin = () => {
        // get the total of members who have admin role
        const adminMembers = members?.filter((memb) => memb.role === adminRef);

        // return whether it's the last admin or not
        return adminMembers?.length === 1;
      };

      return [
        {
          title: "Change Role",
          className: menuButtonCN(),
          onClick: (rowId) => {
            setChangeType("role");
            setSelectedUser(members?.find((user) => user?.id === rowId));
          },
          hideIfLastAdmin:
            row?.original?.id === currentUser?.id ||
            (isLastAdmin() && row?.original?.role === adminRef), // this condition allows to make a user an Admin
        },
        {
          title: "Change Direct Reports",
          className: menuButtonCN(),
          onClick: (rowId) => {
            setChangeType("directReports");
            setSelectedUser(members?.find((user) => user?.id === rowId));
          },
          hideForNonEmployees: true,
        },
        ...(!lockChangingOfStateLocation
          ? [
              {
                title: "Change Employment Type",
                className: menuButtonCN(),
                onClick: (rowId) => {
                  const selectedUserFromRow = members?.find(
                    (user) => user?.id === rowId
                  );

                  setChangeType("employmentType");
                  setSelectedUser(selectedUserFromRow);
                },
              },
            ]
          : []),
        {
          title: "Change Employee ID",
          className: menuButtonCN(),
          onClick: (rowId) => {
            setChangeType("employeeId");
            setSelectedUser(members?.find((user) => user?.id === rowId));
          },
          hideForNonEmployees: true,
        },
        {
          title: "Change Hire Date",
          className: menuButtonCN(),
          onClick: (rowId) => {
            setChangeType("hireDate");
            setSelectedUser(members?.find((user) => user?.id === rowId));
          },
          hideForNonEmployees: true,
        },
        // do not chow this menu if user already have a state of employment assigned
        ...(ptoCategory === PTO_CATEGORY.location &&
        !lockChangingOfStateLocation &&
        !row.original.stateOfEmployment
          ? [
              {
                title: "Change State Of Employment",
                className: menuButtonCN(),
                onClick: (rowId) => {
                  const selectedUserFromRow = members?.find(
                    (user) => user?.id === rowId
                  );

                  setChangeType("stateOfEmployment");
                  setSelectedUser(selectedUserFromRow);
                },
                hideForNonEmployees: true,
              },
            ]
          : []),
        {
          useDynamicTitleForActiveUserStatus: true,
          className: menuButtonCN(!availableSeats),
          onClick: (rowId) => {
            const member = members?.find((user) => user?.id === rowId);
            // can toggle active/inactive if seats are available
            if (availableSeats) {
              setChangeType("active");
              setSelectedUser(member);
              // if no seats available, then can only deactivate
            } else if (!availableSeats && member?.active) {
              setChangeType("active");
              setSelectedUser(member);
            }
          },
          hideForCurrentUser: true,
        },
        {
          title: "Delete",
          className: menuButtonCN(),
          onClick: (rowId) => {
            const selectedMember = [
              members?.find((user) => user?.id === rowId),
            ];
            const id = uuidv4();
            modalDispatch({
              type: ADD_OPEN_MODAL,
              position: {
                centered: true,
              },
              ref: { id },
              modalData: {
                id,
                item: {
                  id,
                  removeFrom: USER_MANAGEMENT,
                  selectedUsers: selectedMember,
                  onSave: onRemoveUser,
                },
              },
              modalType: REMOVE_USER,
            });
          },
          hideForCurrentUser: true,
        },
        {
          title: "Reinvite",
          className: menuButtonCN(),
          onClick: (rowId) => {
            setChangeType("reinvite");
            setSelectedUser(members?.find((user) => user?.id === rowId));
          },
          hideForRegisteredUser: true,
        },
      ];
    },
    [
      currentUser?.id,
      roles,
      isLocked,
      ptoCategory,
      members,
      availableSeats,
      modalDispatch,
      onRemoveUser,
    ]
  );

  const onChangeRole = useCallback((value) => {
    setSelectedUser((prev) => ({
      ...prev,
      role: value?.value,
    }));
  }, []);

  const onChangeUserEmploymentType = useCallback((value) => {
    setSelectedUser((prev) => ({
      ...prev,
      isEmployee: value,
    }));
  }, []);

  const onChangeUserStateOfEmployment = useCallback((value) => {
    setSelectedUser((prev) => ({
      ...prev,
      employeeInfo: {
        ...prev.employeeInfo,
        pto: {
          ...prev.pto,
          category: value?.value ? PTO_CATEGORY.location : PTO_CATEGORY.general,
          locationId: value?.value,
        },
      },
    }));
  }, []);

  const onChangeUserEmployeeId = useCallback((value) => {
    setSelectedUser((prev) => ({
      ...prev,
      employeeInfo: {
        ...prev.employeeInfo,
        id: value,
      },
    }));
  }, []);

  const onChangeUserHireDate = useCallback((value) => {
    const hireDate = new Date(
      Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
    );

    setSelectedUser((prev) => ({
      ...prev,
      employeeInfo: {
        ...prev.employeeInfo,
        hireDate,
      },
    }));
  }, []);

  const onSaveUserStateOfEmployment = useCallback(
    async (memberId) => {
      setIsSaving(true);

      try {
        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          role: selectedUser?.role,
          employeeInfo: selectedUser?.employeeInfo,
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, selectedUser?.employeeInfo, selectedUser?.role, users]
  );

  const onSaveUserDirectReports = useCallback(
    async (memberId, directReport) => {
      setIsSaving(true);

      try {
        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          directReport,
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, users]
  );

  const onSaveUserEmploymentType = useCallback(
    async (memberId) => {
      setIsSaving(true);

      try {
        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          role: selectedUser?.role,
          employeeInfo: selectedUser?.employeeInfo,
          isEmployee: selectedUser?.isEmployee,
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [
      clear,
      patchUser,
      selectedUser?.employeeInfo,
      selectedUser?.isEmployee,
      selectedUser?.role,
      users,
    ]
  );

  const onSaveUserEmploymentId = useCallback(
    async (memberId) => {
      setIsSaving(true);

      try {
        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          employeeInfo: selectedUser?.employeeInfo,
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, selectedUser?.employeeInfo, users]
  );
  const onSaveUserHireDate = useCallback(
    async (memberId) => {
      setIsSaving(true);

      try {
        const hd = new Date(selectedUser?.employeeInfo?.hireDate);
        const newHd = new Date(
          Date.UTC(hd.getFullYear(), hd.getMonth(), hd.getDate())
        );

        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          employeeInfo: {
            ...selectedUser?.employeeInfo,
            hireDate: newHd,
          },
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, selectedUser?.employeeInfo, users]
  );

  const onSaveUserRole = useCallback(
    async (memberId) => {
      setIsSaving(true);

      try {
        const oldUserInfo = userDict[`User/${memberId}`];
        const newUserInfo = {
          ...oldUserInfo,
          role: selectedUser?.role,
        };

        // If current user's role is being changed we need to update role permissions across the app
        let newUserPermissions = {};
        if (currentUser.id === newUserInfo.id) {
          // Add permissions for optimistic updating of user
          const {
            data: { permissions },
          } = await RoleAPI.getById(newUserInfo.role.split("/")[1]);
          newUserPermissions = permissions;
        }

        await patchUser({
          oldUserInfo,
          newUserInfo,
          newUserPermissions,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, selectedUser?.role, userDict, currentUser.id]
  );

  const onSaveUserStatus = useCallback(
    async (memberId) => {
      setIsSaving(true);
      try {
        const originalUser = users.find((user) => user.id === memberId);
        const updatedUser = {
          ...originalUser,
          active: !originalUser.active,
        };

        await patchUser({
          oldUserInfo: originalUser,
          newUserInfo: updatedUser,
        });

        clear();
      } catch (err) {
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [clear, patchUser, users]
  );

  const onReinviteUser = useCallback(
    async (memberId) => {
      setIsSaving(true);
      try {
        await UserAPI.getWOP(`${memberId}/$resendinvitation`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Resend invitation failed ", err);
      }

      setIsSaving(false);
      clear();
    },
    [clear]
  );

  return {
    isSaving,
    isLoading,
    states,
    roles,
    changeType,
    availableSeats,
    activeUsersCount,
    directReportOptions,
    members,
    ptoCategory,
    originalSubscription,
    selectedUser,
    clear,
    menuButtonCN,
    navigate,
    rowAssociationMenuList,
    onChangeUserEmploymentType,
    onChangeRole,
    onChangeUserStateOfEmployment,
    onChangeUserEmployeeId,
    onChangeUserHireDate,
    onSaveUserStateOfEmployment,
    onSaveUserDirectReports,
    onSaveUserEmploymentType,
    onSaveUserEmploymentId,
    onSaveUserHireDate,
    onSaveUserRole,
    onSaveUserStatus,
    onReinviteUser,
    setActiveUsersCount,
    setSelectedUser,
  };
};

export default useMemberTableData;
